if (!customElements.get('ak-product-ribbon')) {
  customElements.define('ak-product-ribbon', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.viewport = this.querySelector('[data-ribbon-viewport]');
      this.track = this.querySelector('[data-ribbon-track]');
      this.list = this.querySelector('[data-ribbon-products]');
      this.toggle = this.querySelector('[data-ribbon-toggle]');
      this.controls = this.querySelector('[data-ribbon-controls]');
      this.more = this.querySelector('[data-ribbon-load-more]');
      this.status = this.querySelector('[data-ribbon-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.manualPause = this.dataset.autoplay !== 'true';
      this.hovered = false;
      this.focused = false;
      this.visible = true;
      this.ready = false;
      this.distance = 0;
      this.position = this.viewport.scrollLeft;
      this.speed = Math.max(10, Math.min(50, Number(this.dataset.speed) || 20));
      const options = { signal: this.controller.signal };
      this.toggle.addEventListener('click', () => {
        this.manualPause = !this.manualPause;
        this.updateMotion();
      }, options);
      this.viewport.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.updateMotion();
      }, options);
      this.viewport.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.updateMotion();
      }, options);
      this.viewport.addEventListener('focusin', () => {
        this.focused = true;
        this.updateMotion();
      }, options);
      this.viewport.addEventListener('focusout', (event) => {
        this.focused = this.viewport.contains(event.relatedTarget);
        this.updateMotion();
      }, options);
      const pauseForInteraction = () => {
        this.manualPause = true;
        this.updateMotion();
      };
      this.viewport.addEventListener('pointerdown', pauseForInteraction, { ...options, passive: true });
      this.viewport.addEventListener('wheel', pauseForInteraction, { ...options, passive: true });
      this.viewport.addEventListener('keydown', (event) => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) pauseForInteraction();
      }, options);
      this.querySelector('[data-ribbon-previous]').addEventListener('click', () => this.step(-1), options);
      this.querySelector('[data-ribbon-next]').addEventListener('click', () => this.step(1), options);
      document.addEventListener('visibilitychange', () => this.updateMotion(), options);
      this.motion.addEventListener('change', () => this.rebuildLoop(), options);
      this.intersection = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.updateMotion();
      }, { rootMargin: '120px' });
      this.intersection.observe(this);
      this.resize = new ResizeObserver(() => this.rebuildLoop());
      this.resize.observe(this.viewport);
      this.resize.observe(this.list);
      this.loadCatalog();
    }

    async loadCatalog() {
      let next = this.dataset.nextPage;
      const visited = new Set();
      const seen = new Set([...this.list.querySelectorAll('[data-product-id]')].map((item) => item.dataset.productId));
      try {
        while (next && this.isConnected) {
          const url = new URL(next, window.location.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          if (url.origin !== window.location.origin || visited.has(url.href)) throw new Error('Invalid catalog pagination');
          visited.add(url.href);
          const response = await fetch(url.href, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Catalog page unavailable');
          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const ribbon = [...page.querySelectorAll('ak-product-ribbon')].find((element) => element.dataset.sectionId === this.dataset.sectionId);
          if (!ribbon) throw new Error('Catalog section unavailable');
          const products = ribbon.querySelector('[data-ribbon-products]');
          if (!products) throw new Error('Catalog products unavailable');
          const fragment = document.createDocumentFragment();
          for (const item of products.querySelectorAll('[data-product-id]')) {
            if (seen.has(item.dataset.productId)) continue;
            seen.add(item.dataset.productId);
            fragment.append(item);
          }
          this.list.append(fragment);
          next = ribbon.dataset.nextPage;
          if (this.more && next) this.more.href = next;
        }
        if (this.more) this.more.hidden = true;
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = this.dataset.loadError;
        this.status.hidden = false;
        if (this.more) this.more.hidden = false;
      }
      if (!this.isConnected) return;
      this.ready = true;
      this.rebuildLoop();
    }

    rebuildLoop() {
      if (!this.ready) return;
      this.stopFrame();
      this.track.querySelectorAll('[data-ribbon-clone]').forEach((clone) => clone.remove());
      this.distance = this.list.getBoundingClientRect().width;
      const count = this.list.querySelectorAll('[data-product-id]').length;
      const canLoop = count > 1 && !this.motion.matches && this.dataset.autoplay === 'true' && this.distance > 0;
      this.toggle.hidden = !canLoop;
      this.controls.hidden = count < 2;
      if (canLoop) {
        const copies = Math.max(1, Math.ceil(this.viewport.clientWidth / this.distance));
        for (let index = 0; index < copies; index += 1) {
          const clone = this.list.cloneNode(true);
          clone.removeAttribute('data-ribbon-products');
          clone.setAttribute('data-ribbon-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
          this.track.append(clone);
        }
        this.viewport.scrollLeft %= this.distance;
      }
      this.looping = canLoop;
      this.updateMotion();
    }

    step(direction) {
      this.manualPause = true;
      this.updateMotion();
      const card = this.list.querySelector('[data-product-id]');
      const gap = Number.parseFloat(getComputedStyle(this.list).gap) || 0;
      const amount = card ? card.getBoundingClientRect().width + gap : this.viewport.clientWidth * 0.8;
      if (this.looping && direction < 0 && this.viewport.scrollLeft < amount) this.viewport.scrollLeft += this.distance;
      if (this.looping && direction > 0 && this.viewport.scrollLeft >= this.distance) this.viewport.scrollLeft -= this.distance;
      this.viewport.scrollBy({ left: direction * amount, behavior: this.motion.matches ? 'auto' : 'smooth' });
    }

    updateMotion() {
      const paused = this.manualPause || this.motion.matches;
      this.classList.toggle('is-paused', paused);
      this.toggle.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      const shouldRun = this.ready && this.looping && !paused && !this.hovered && !this.focused && this.visible && !document.hidden;
      if (!shouldRun) {
        this.stopFrame();
        return;
      }
      if (this.frame) return;
      this.position = this.viewport.scrollLeft % this.distance;
      this.previousTime = 0;
      const animate = (time) => {
        if (this.previousTime) {
          const elapsed = Math.min(time - this.previousTime, 50);
          this.position = (this.position + this.speed * elapsed / 1000) % this.distance;
          this.viewport.scrollLeft = this.position;
        }
        this.previousTime = time;
        this.frame = requestAnimationFrame(animate);
      };
      this.frame = requestAnimationFrame(animate);
    }

    stopFrame() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.previousTime = 0;
    }

    disconnectedCallback() {
      this.stopFrame();
      this.controller?.abort();
      this.intersection?.disconnect();
      this.resize?.disconnect();
      this.track?.querySelectorAll('[data-ribbon-clone]').forEach((clone) => clone.remove());
      this.controller = null;
    }
  });
}
